import styled from "styled-components";
import { DhlText, DhlSwitch } from "@dhl-official/react-library";
import { breakpoints, grid } from "root/libs/ui-styleguide/src";
import { IconButton, TextAndIcon } from "root/libs/ui-components/src";

export const TradeLaneWrapper = styled.div`
  background-color: var(--dui-color-white-500);
  box-shadow: var(--dui-size-space-0) var(--gaq-size-space-3px)
    var(--dui-size-space-4x) var(--dui-color-gray-300);
  border-radius: var(--dui-size-space-2x);
  margin-bottom: var(--gaq-size-space-30px);
  padding: var(--dui-size-space-10x) var(--dui-size-space-0)
    var(--dui-size-space-5x);

  @media screen and (min-width: ${breakpoints.tablet}) {
    margin-bottom: var(--gaq-size-space-50px);
  }

  @media print {
    box-shadow: none;
  }
`;

export const TradeLaneFormContentWrapper = styled("div")(
  ({ reimaginePostcodesEnabled }) => {
    const tablet = {
      padding: `0 ${grid.columns.width.m} 0 ${grid.columns.width.m}`,
      gap: 0,
    };

    const styles = {
      boxSizing: "border-box",
      display: "flex",
      flexWrap: "wrap",
      position: "relative",

      [`@media screen and (min-width: ${breakpoints.tablet})`]: tablet,
    };

    if (reimaginePostcodesEnabled) {
      styles.flexFlow = "column";
      styles.gap = "var(--dui-size-space-16x)";
      styles.marginTop = "var(--dui-size-space-10x)";

      styles["> fieldset"] = {
        display: "flex",
        flexDirection: "column",

        "&:after": {
          display: "none",
        },
      };

      styles[`@media screen and (min-width: ${breakpoints.tablet})`] = {
        ...tablet,
        alignItems: "stretch",
        flexFlow: "row",

        "& > fieldset": {
          width: "auto",
          flex: "1",
          order: "unset",
        },
      };
    }

    return styles;
  }
);

export const TradeLaneImportExportToggleWrapper = styled.div`
  @media screen and (min-width: ${breakpoints.tablet}) {
    padding: 0 ${grid.columns.width.m};
    text-align: right;
  }
`;

export const TradeLaneTooltipWrapper = styled.div`
  @media screen and (min-width: ${breakpoints.tablet}) {
    padding: 0 ${grid.columns.width.m};
  }
`;

export const DirectionParagraph = styled(DhlText)`
  & > *:is(p, span) {
    margin-bottom: ${({ reimaginePostcodesEnabled }) =>
      `var(--dui-size-space-${reimaginePostcodesEnabled ? 12 : 4}x)`};
  }
`;

export const DirectionHeadlineWrapper = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;

  @media screen and (min-width: ${breakpoints.desktop}) {
    flex-wrap: nowrap;
  }

  @media print {
    line-height: 0px;
  }
`;

export const OriginContainer = styled.fieldset`
  display: block;
  width: 100%;
  order: 1;

  @media screen and (min-width: ${breakpoints.tablet}) {
    &:after {
      content: "";
      display: block;
      margin-bottom: var(--dui-size-space-10x);
    }
  }
`;

export const FlipIconButtonWrapper = styled.div`
  order: 2;
  width: 100%;
`;

export const ToggleTextAndIcon = styled(TextAndIcon)`
  display: inline-flex !important;
  text-align: left;

  & > dhl-text {
    flex-grow: 1;

    & > *:is(p, span) {
      padding: 0 !important;
      font-size: var(--dui-size-font-md) !important;
      line-height: var(--dui-size-line-height-lg) !important;
      position: unset;
    }
  }

  & > span {
    padding-right: 0;
  }

  @media screen and (max-width: ${breakpoints.tablet}) {
    display: flex !important;
    align-items: flex-start;
    justify-content: flex-start !important;
    // Block wrapping the SwitchField + Tooltip label
    & > span {
      width: 100%;
    }

    & > div {
      margin-top: var(--gaq-size-space--6px) !important;
    }
  }
`;

export const ToggleField = styled(DhlSwitch)`
  outline: 0 !important;
  border: 0 !important;
  border-radius: var(--dui-size-radius-md);

  /* RTL Fix */
  & > label > input {
    /*! @noflip */
    left: -9999px;
    /*! @noflip */
    right: unset;
  }

  & > div {
    outline: 0 !important;
    border: 0 !important;
  }

  & div > label > span {
    font-size: var(--dui-size-font-md) !important;
    line-height: var(--dui-size-line-height-lg) !important;
    border: 0 !important;
    padding: var(--dui-size-space-5x) 0;

    &:active {
      border: 0 !important;
    }
  }

  @media screen and (max-width: ${breakpoints.tablet}) {
    // Block with the SwitchField wrapper
    & div > label {
      padding: 0 0 1.4rem 0 !important;
      display: flex;
      flex-wrap: wrap;
    }

    // Block with the SwitchField control
    & div > label > div {
      display: block !important;
      position: relative !important;
      order: 1 !important;
    }

    // Block with the SwitchField text
    & div > label > span {
      margin-left: 0 !important;
      display: block;
      width: 100%;
      padding: 0 !important;
      margin: 0;
    }
  }

  & div > label > div {
    // Transforms SwitchField in small pill for desktop
    @media screen and (min-width: ${breakpoints.tablet}) {
      top: var(--dui-size-space-2x) !important;
      transform: scale(0.7) translateX(5px) translateY(7px) !important;
    }
  }
`;

export const StyledFlipIconButton = styled(IconButton)`
  transform: rotate(90deg);
  order: 2;
  position: relative;
  left: -39px;
  margin-bottom: 0;
  transition: top 0.4s ease-in-out;

  @media screen and (min-width: ${breakpoints.tablet}) {
    position: absolute;
    align-self: start;
    // Fix for flags displaying on top of flip-icon
    // This button will be removed once we finish the import/export test
    z-index: 3;
    left: 0;
    top: ${(props) => (props.isOriginValid ? "116px" : "140px")};
    margin-top: var(--dui-size-space-5x);
    /* RTL aligment Fix */
    transform: rotate(90deg)
      translate(-50%, ${(props) => (props.rtlEnabled ? "-50%" : "50%")});
  }
`;

export const DestinationContainer = styled.fieldset`
  display: block;
  width: 100%;
  order: 3;
`;

export const FeedbackMessageContainer = styled.div`
  margin-top: var(--dui-size-space-10x);

  @media screen and (min-width: ${breakpoints.tablet}) {
    margin-left: ${grid.columns.width.m};
    margin-right: ${grid.columns.width.m};
    margin-top: var(--gaq-size-space-30px);
  }

  @media screen and (min-width: ${breakpoints.desktop}) {
    margin-left: ${grid.columns.width.l};
    margin-right: ${grid.columns.width.l};
  }
`;
